import {
  DetailedHTMLProps,
  forwardRef,
  LegacyRef,
  TextareaHTMLAttributes,
} from "react";

import { useWatch } from "react-hook-form";

import { useGetProduct } from "queries/legacy/product-queries";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";

import theme from "styles/theme.module.scss";

import styles from "./MultiLineText.module.scss";

type MultiLineTextProps = DetailedHTMLProps<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
> & { name: string };
// ^ Make `name` a required prop

/**
 * Textarea for multi-line fields.
 *
 * Uses a pseudo element to automatically grow as the user types;
 * see `./MultiLineText.module.scss` for more.
 *
 * Spreads all textarea props.
 */
const MultiLineText = forwardRef(
  (
    { maxLength, onChange, ...props }: MultiLineTextProps,
    ref: LegacyRef<HTMLTextAreaElement> | undefined
  ) => {
    const value = useWatch({
      name: props.name,
    });

    const { data: product } = useGetProduct();

    const interpolate = useInterpolate();

    return (
      <div className={styles.autoGrowWrapper} data-replicated-value={value}>
        <textarea
          ref={ref}
          className={[styles.textarea, props.className].join(" ")}
          rows={1}
          onChange={(e) => {
            onChange?.(e);
          }}
          {...props}
        />

        {/* We don't spread maxLength into the textarea so input isn't capped. Let the form handle overflow */}
        {maxLength &&
          (() => {
            const charsLeft = maxLength - String(value).length;

            return (
              <span
                className={styles.charactersLeft}
                style={{
                  ...(charsLeft < 0 && {
                    color: theme.colorCherryCola,
                  }),
                }}
              >
                {interpolate(
                  product?.content.canvas.characters_left_label ??
                    CANVAS_FALLBACK_CONTENT.characters_left_label,
                  { number: charsLeft }
                )}
              </span>
            );
          })()}
      </div>
    );
  }
);

export default MultiLineText;
